import {
  BulkUpdatePartialFailDto,
  KSendSuccessResponse,
  UpdateRowsRequestDto,
} from '@asset-metrix/portal-gateway-client-js';
import { isObject } from '@asset-metrix/portal-ui';
import { MutationFunction } from '@tanstack/react-query';

import { extractErrorMessage } from '../../utils/errorMessage';
import { HttpError } from '../errors';
import { portalGatewayClient } from '../portalGatewayClient';

const PARTIAL_BULK_UPDATE_FAIL_CODE = 'bulk-update-partial-exception' as const;

export type UpdateTableRowsPayload = Pick<
  UpdateRowsRequestDto,
  'data' | 'context'
>;

function isBulkUpdatePartialFailDto(
  value: unknown
): value is BulkUpdatePartialFailDto {
  return (
    isObject(value) &&
    value.status === 'fail' &&
    value.code === PARTIAL_BULK_UPDATE_FAIL_CODE
  );
}

function patchTableRowsErrorMessage(
  error: unknown,
  defaultMessage: string
): string {
  if (!isBulkUpdatePartialFailDto(error)) {
    return extractErrorMessage(error, defaultMessage);
  }

  const detail = error.data.message?.trim();
  return detail ? `${error.message}\n${detail}` : error.message;
}

/** Partial bulk PATCH: some rows updated, some failed — refresh table data only. */
export function shouldRefreshTableAfterBulkRowUpdateError(
  error: unknown
): boolean {
  if (!(error instanceof HttpError)) {
    return false;
  }

  return (
    isBulkUpdatePartialFailDto(error.data) && error.data.data.succeededCount > 0
  );
}

export const updateTableRows: MutationFunction<
  KSendSuccessResponse,
  {
    contentId: string;
    rowIds: string[];
    data: UpdateTableRowsPayload;
  }
> = async ({ contentId, rowIds, data }) => {
  const response = await portalGatewayClient.table.patchTableRows(contentId, {
    ...data,
    rowIds,
  });

  if (!response.ok) {
    throw new HttpError(
      patchTableRowsErrorMessage(response.error, 'Error updating rows'),
      response
    );
  }

  return response.data;
};
